import { Page } from '../../../components/Page/Page';
import {
  Article,
  Category,
  fetchCategories,
  fetchCategory,
  fetchCategoryArticles,
} from '../../../api/blogs';
import { Button, Grid } from '@mui/material';
import { Section } from '../../../components/Section/Section';
import { HighLightedText } from '../../../components/HighLightedText/HighLightedText';
import { ArticleCard } from '../../../components/Kennisbank/ArticleCard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppLink from '../../../components/Link/AppLink';
import { KENNISBANK_ROUTE } from '../../routes';

interface Props {
  category: Category;
  articles: Article[];
}

export default function CategoryPage(props: Props) {
  const { category, articles } = props;
  return (
    <Page
      title={`${category.name} | Kennisbank`}
      seo={{
        description: category.description,
        canonical: `https://www.appcenternederland.nl/kennisbank/categorieen/${category.slug}`,
        openGraph: {
          type: 'website',
          locale: 'nl_NL',
          title: `${category.name} | Kennisbank | AppCenter Nederland`,
          description: category.description,
          images: [
            {
              url: 'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/featured_img_home_v2.jpg',
              secureUrl:
                'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/featured_img_home_v2.jpg',
              width: 1920,
              height: 1080,
              alt: category.description,
              type: 'image/jpeg',
            },
          ],
          site_name: 'AppCenter Nederland',
          url: `https://www.appcenternederland.nl/kennisbank/categorieen/${category.slug}`,
        },
      }}
    >
      <Section1 category={category} articles={articles} />
    </Page>
  );
}

interface Section1Props {
  category: Category;
  articles: Article[];
}

function Section1(props: Section1Props) {
  const { category, articles } = props;

  return (
    <Section
      position="center"
      contentBeforeTitle={
        <Button
          component={AppLink}
          href={KENNISBANK_ROUTE}
          startIcon={<ArrowBackIcon />}
        >
          Terug naar overzicht
        </Button>
      }
      title={
        <>
          <HighLightedText
            TypographyProps={{ color: 'secondary' }}
            text={category.name}
          />
        </>
      }
      subtitle={category.description}
    >
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {articles.map((article, key) => (
          <Grid item key={key} xs={12} sm={12} md={4} lg={4}>
            <ArticleCard article={article} />
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

// This also gets called at build time
export async function getStaticProps({ params }) {
  const category = await fetchCategory(params.slug);
  const articles = await fetchCategoryArticles(params.slug);

  return {
    props: {
      category: category,
      articles: articles,
    },
  };
}

// This function gets called at build time
export async function getStaticPaths() {
  // Call an external API endpoint to get posts
  // Fetch data from external API
  const categories = await fetchCategories();

  // Get the paths we want to pre-render based on posts
  const paths = categories.map((category) => ({
    params: { slug: category.slug },
  }));
  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
}
